import { useCallback, useEffect, useMemo, useLayoutEffect, useRef, useState } from 'react';
import { useTheme, Box } from '@mui/material';
import {
  ReactFlow,
  MiniMap,
  Controls,
  ControlButton,
  Background,
  addEdge,
  useReactFlow,
} from '@xyflow/react';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useDrop } from 'react-dnd';
import { componentMap } from 'src/features/customDashboard/utils/componentMap';
import '@xyflow/react/dist/style.css';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';
import { ComponentType } from 'src/types/SCADA/componentsTypes';
import { EditorMode } from 'src/features/customDashboard/types';
import { useResponsive } from 'src/hooks/useResponsive';
import { v4 as uuidv4 } from 'uuid';
import { FullscreenToggleButton } from 'src/components';

enum BackgroundVariant {
  Lines = 'lines',
  Dots = 'dots',
  Cross = 'cross',
}

const DESKTOP_CANVAS_HEIGHT = '750px';
const MIN_CANVAS_HEIGHT = 280;
const MOBILE_BOTTOM_GAP = 5;

type Props = {
  mode: EditorMode;
};

export const CanvasScreen = ({ mode }: Props) => {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const isPreviewMode = mode === 'preview';

  const { fitView, screenToFlowPosition } = useReactFlow();

  const [isInteractive, setIsInteractive] = useState(!isPreviewMode);

  useEffect(() => {
    setIsInteractive(!isPreviewMode);
  }, [isPreviewMode]);

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    selectNode,
    setDrawerType,
    deleteNode,
    showMiniMap,
  } = useCustomDashboardContext();

  useLayoutEffect(() => {
    if (isMobile) return;
    const watermark = document.querySelector('.react-flow__attribution');
    if (watermark) {
      watermark.remove();
    }
  }, [isMobile]);

  useEffect(() => {
    const refit = () => fitView({ padding: 0.1, minZoom: 0.1, duration: 200 });
    window.addEventListener('resize', refit);
    window.addEventListener('orientationchange', refit);
    return () => {
      window.removeEventListener('resize', refit);
      window.removeEventListener('orientationchange', refit);
    };
  }, [fitView]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === '1'
          ? {
              ...node,
              data: { ...node.data, label: '' },
            }
          : node
      )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback(
    (item: any, monitor: any) => {
      const offset = monitor.getClientOffset();
      if (!offset) return;

      const flowPosition = screenToFlowPosition({
        x: offset.x,
        y: offset.y,
      });

      const defaultsByType: Record<string, { width: number; height: number }> = {
        [ComponentType.TextLabel]: { width: 50, height: 20 },
        [ComponentType.LiveTagLabel]: { width: 110, height: 20 },
        [ComponentType.Well]: { width: 180, height: 380 },
        [ComponentType.Tower]: { width: 370, height: 380 },
        [ComponentType.WaterLevelShape]: { width: 180, height: 380 },
        [ComponentType.Valve]: { width: 69, height: 41 },
        [ComponentType.ImageUpload]: { width: 100, height: 100 },
        [ComponentType.Rotor]: { width: 80, height: 80 },
      };

      const size = defaultsByType[item.type] ?? { width: 120, height: 80 };

      const newNode = {
        id: uuidv4(),
        type: item.type,
        position: flowPosition,
        selected: true,
        data: {
          label: item.name,
          drawerType: item.drawerType,
          ...(item.type === ComponentType.Rotor && { subType: ComponentType.Rotor }),
          ...(item.type === ComponentType.TextLabel && { color: theme.palette.common.black }),
          ...(item.type === ComponentType.TextLabel && {
            backgroundColor: theme.palette.grey[300],
          }),
          ...(item.type === ComponentType.TextLabel && { text: 'Label' }),
        },
        measured: size,
        ...(item.type === ComponentType.Rectangle && { isLockRatio: false }),
      };
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(newNode));
      selectNode(newNode);
      setDrawerType(newNode.data.drawerType);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes.length, selectNode]
  );

  const [{ canDrop }, drop] = useDrop({
    accept: 'node',
    drop: onDrop,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      drop(node);
    },
    [drop]
  );

  // On mobile/tablet the canvas must never grow past the visible area, so it is capped to the
  // viewport height minus whatever the page renders above it (headers, controller bar, ...).
  useLayoutEffect(() => {
    if (!isMobile) {
      setAvailableHeight(null);
      return;
    }

    const measure = () => {
      const node = containerRef.current;
      if (!node) return;

      const offsetTop = node.getBoundingClientRect().top + window.scrollY;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

      setAvailableHeight(
        Math.max(viewportHeight - offsetTop - MOBILE_BOTTOM_GAP, MIN_CANVAS_HEIGHT)
      );
    };

    measure();

    // Content above the canvas can change height without a window resize (fullscreen toggle,
    // header controller bar, ...), so observe the document as well.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [isMobile]);

  useEffect(() => {
    if (availableHeight === null) return;
    fitView({ padding: 0.1, minZoom: 0.1, duration: 200 });
  }, [availableHeight, fitView]);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );
  const nodeTypes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(componentMap).map(([key, Component]) => [
          key,
          (nodeProps: any) => (
            <Component
              {...nodeProps}
              data={nodeProps.data}
              selected={nodeProps.selected}
              width={nodeProps.width}
              height={nodeProps.height}
            />
          ),
        ])
      ),
    []
  );

  const onEdgeClick = (event: any, edge: any) => {
    event.stopPropagation();
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  };

  const onNodeClick = useCallback(
    (event: any, node: any) => {
      event.stopPropagation();
      selectNode(node);
    },
    [selectNode]
  );

  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        height: isMobile ? '100dvh' : DESKTOP_CANVAS_HEIGHT,
        ...(isMobile && {
          marginBottom: `${MOBILE_BOTTOM_GAP}px`,
          ...(availableHeight !== null && { maxHeight: `${availableHeight}px` }),
        }),
      }}
      ref={setContainerRef}
    >
      <Box
        data-sm="canvas-test-id"
        sx={{
          flexGrow: 1,
          position: 'relative',
          background: canDrop ? theme.palette.primary.lighter : '#f0f0f0',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <FullscreenToggleButton />
        </Box>
        <ReactFlow
          fitView
          minZoom={0.1}
          fitViewOptions={{ padding: 0.1, minZoom: 0.1 }}
          nodes={nodes}
          edges={edges}
          onNodeClick={onNodeClick}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={onEdgeClick}
          onConnect={onConnect}
          panOnDrag={true}
          nodeTypes={nodeTypes}
          nodesDraggable={isInteractive && !isPreviewMode}
          nodesConnectable={isInteractive && !isPreviewMode}
          elementsSelectable={isInteractive && !isPreviewMode}
          snapToGrid={true}
          snapGrid={[1, 1]}
          onNodesDelete={(deletedNodes) => {
            deletedNodes.forEach((node) => deleteNode(node.id));
          }}
        >
          <Controls showFitView={false} showInteractive={false} className="custom-controls">
            <ControlButton
              onClick={() =>
                fitView({
                  padding: 0.2,
                  minZoom: 0.1,
                  duration: 400,
                })
              }
            >
              <CenterFocusStrongIcon />
            </ControlButton>

            <ControlButton
              onClick={() => {
                if (!isPreviewMode) setIsInteractive((prev) => !prev);
              }}
              style={isPreviewMode ? { cursor: 'default', pointerEvents: 'none' } : undefined}
            >
              {!isPreviewMode && isInteractive ? <LockOpenIcon /> : <LockIcon />}
            </ControlButton>
          </Controls>
          {showMiniMap && <MiniMap />}
          <Box style={{ display: isPreviewMode ? 'none' : 'block' }}>
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </Box>
        </ReactFlow>
      </Box>
    </div>
  );
};
